import { Injectable, inject, signal, effect } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { environment } from '../../../environments/environment';

export interface UserProfile {
  id: string;
  tenantId: string;
  name: string;
  email: string;
  roles: string[];
  groups: string[];
  managedDepartments?: any[]; // Array of department Location objects
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly msalService = inject(MsalService);

  // Reactive state with signals
  readonly isAuthenticated = signal(false);
  readonly currentUser = signal<UserProfile | null>(null);
  readonly loading = signal(false);

  private departmentsLoaded = false;

  constructor() {
    this.initializeAuthState();

    // Load departments when authentication state changes
    effect(() => {
      if (this.isAuthenticated() && !this.departmentsLoaded) {
        this.loadManagedDepartments();
      }
    }, { allowSignalWrites: true });
  }

  /**
   * Initialize authentication state
   */
  private initializeAuthState(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      this.updateAuthState(accounts[0]);
    }

    // Subscribe to login success
    this.msalService.instance.enableAccountStorageEvents();
  }

  /**
   * Update authentication state from account info
   */
  private updateAuthState(account: AccountInfo | null): void {
    if (account) {
      const userProfile: UserProfile = {
        id: account.localAccountId,
        tenantId: account.tenantId,
        name: account.name || account.username,
        email: account.username,
        roles: this.extractRoles(account),
        groups: this.extractGroups(account),
      };
      this.currentUser.set(userProfile);
      this.isAuthenticated.set(true);
    } else {
      this.currentUser.set(null);
      this.isAuthenticated.set(false);
    }
  }

  /**
   * Extract roles from account claims
   */
  private extractRoles(account: AccountInfo): string[] {
    const roles: string[] = [];
    const claims = account.idTokenClaims as any;

    if (claims?.roles) {
      roles.push(...(Array.isArray(claims.roles) ? claims.roles : [claims.roles]));
    }

    return roles;
  }

  /**
   * Extract groups from account claims
   */
  private extractGroups(account: AccountInfo): string[] {
    const groups: string[] = [];
    const claims = account.idTokenClaims as any;

    if (claims?.groups) {
      groups.push(...(Array.isArray(claims.groups) ? claims.groups : [claims.groups]));
    }

    return groups;
  }

  /**
   * Login user
   */
  async login(): Promise<void> {
    this.loading.set(true);
    try {
      const result: AuthenticationResult = await this.msalService.instance.loginPopup({
        scopes: environment.azure.scopes,
      });

      this.msalService.instance.setActiveAccount(result.account);
      this.updateAuthState(result.account);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    this.loading.set(true);
    try {
      await this.msalService.instance.logoutPopup();
      this.updateAuthState(null);
      this.departmentsLoaded = false; // Reset flag on logout
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.currentUser();
    return user?.roles.includes(role) || false;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  /**
   * Check if user is in specific group
   */
  inGroup(groupId: string): boolean {
    const user = this.currentUser();
    return user?.groups.includes(groupId) || false;
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.inGroup(environment.securityGroups.admin);
  }

  /**
   * Check if user is gestor
   */
  isGestor(): boolean {
    return this.inGroup(environment.securityGroups.gestor) || this.isAdmin();
  }

  /**
   * Check if user is inventory admin (can access all operations)
   */
  isInventoryAdmin(): boolean {
    return this.isAdmin();
  }

  /**
   * Check if user is department manager (gestor but not admin)
   */
  isDepartmentManager(): boolean {
    return this.isGestor() && !this.isAdmin();
  }

  /**
   * Check if user is LAF user (can create purchase requests and view their orders)
   */
  isLafUser(): boolean {
    return this.inGroup(environment.securityGroups.lafUsers) && !this.isGestor();
  }

  /**
   * Get IDs of departments managed by current user
   */
  getManagedDepartmentIds(): string[] {
    return this.currentUser()?.managedDepartments?.map((d: any) => d.id) || [];
  }

  /**
   * Load departments managed by current user from backend
   */
  async loadManagedDepartments(): Promise<void> {
    // Prevent multiple loads
    if (this.departmentsLoaded) {
      return;
    }

    const user = this.currentUser();
    console.log('[AuthService] Loading managed departments for user:', {
      email: user?.email,
      isGestor: this.isGestor(),
      isAdmin: this.isAdmin(),
      isDepartmentManager: this.isDepartmentManager()
    });

    // Only for department managers (gestors who are not admins)
    if (!this.isDepartmentManager()) {
      console.log('[AuthService] User is not a department manager, skipping department load');
      this.departmentsLoaded = true;
      return;
    }

    try {
      const token = await this.getAccessToken();
      const userEmail = user?.email?.toLowerCase();

      if (!userEmail) {
        console.warn('[AuthService] No email found for user, cannot load departments');
        this.departmentsLoaded = true;
        return;
      }

      // Use getLocations endpoint with filter for department type
      // We'll filter by email on the client side since the backend doesn't support email filter yet
      console.log('[AuthService] Fetching locations (departments) from:', `${environment.api.readUrl}/query/catalog/locations`);

      const response = await fetch(
        `${environment.api.readUrl}/query/catalog/locations`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[AuthService] Locations loaded, filtering for departments managed by user');

        // Filter locations to find departments where the user is the manager
        // Note: getLocations returns { items: [...] }
        const departments = (data.items || []).filter((loc: any) =>
          loc.locationType === 'department' &&
          loc.email?.toLowerCase() === userEmail &&
          loc.status === 'active'
        );

        console.log('[AuthService] Departments found:', departments);

        this.currentUser.update(user => user ? {
          ...user,
          managedDepartments: departments
        } : null);

        if (departments.length === 0) {
          console.warn('[AuthService] No departments found for user. Check if:');
          console.warn('  1. A location with type="department" exists');
          console.warn('  2. Location email matches user email exactly:', userEmail);
          console.warn('  3. Location status is "active"');
          console.warn('  4. Location isDeleted is false');
        }

        this.departmentsLoaded = true;
      } else {
        console.error('[AuthService] Failed to load locations:', response.status, await response.text());
        this.departmentsLoaded = true;
      }
    } catch (error) {
      console.error('[AuthService] Error loading managed departments:', error);
      this.departmentsLoaded = true;
    }
  }

  /**
   * Get current user's email
   */
  getUserEmail(): string | null {
    return this.currentUser()?.email || null;
  }

  /**
   * Get access token for API calls
   */
  async getAccessToken(): Promise<string> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      throw new Error('No active account');
    }

    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        scopes: environment.azure.apiScopes,
        account,
      });
      return result.accessToken;
    } catch (error) {
      console.error('Token acquisition failed:', error);
      // Fallback to interactive token acquisition
      const result = await this.msalService.instance.acquireTokenPopup({
        scopes: environment.azure.apiScopes,
      });
      return result.accessToken;
    }
  }
}
